import type { PortfolioRow } from "../components/portfolio/columns/columnTypes";
import { normalizeAccountNumber } from "../components/portfolio/columns/formatters";
import type { EnrichedHoldingRow } from "../hooks/useStrategyHoldings";

/**
 * Convert modeled strategy holdings into synthetic PortfolioRow values.
 * The model weight is stored in targetWeightPct because PortfolioRow has no
 * dedicated model field. Existing dashboard callers also mirror it into the
 * current field; Alpha Positions opts out because that field means live IBKR.
 */
export function holdingsToPortfolioRows(
  holdings: EnrichedHoldingRow[],
  nlv: number | string | null | undefined,
  options: { modelOnly?: boolean } = {}
): PortfolioRow[] {
  const normalizedNlv = normalizeAccountNumber(nlv);
  return holdings.map((h): PortfolioRow => {
    const targetDollar = normalizedNlv != null ? (h.netWeight / 100) * normalizedNlv : null;
    return {
      conId: h.conId,
      symbol: h.ticker,
      currency: h.currency,
      shares: 0,
      avgCost: 0,
      mktPrice: 0,
      mktValue: 0,
      realizedPnl: 0,
      unrealizedPnl: 0,
      group: h.contributions.map((c) => c.productName).join(", "),
      sector: null,
      secType: h.contractType === "OPTION" ? "OPT" : "STK",
      priceUsd: null,
      mktValueUsd: targetDollar,
      unrealizedPnlUsd: null,
      currentWeightPct: options.modelOnly ? null : h.netWeight,
      targetWeightPct: h.netWeight,
      targetQty: null,
      rawTargetQty: null,
      effectiveTargetQty: null,
      tradeQty: null,
      tradeValue: null,
      tradeValueUsd: null,
      fxRate: null,
      tradeAction: null,
      minSize: null,
      isSynthetic: false,
      isInTarget: true,
      timeZoneId: null,
      userDiffId: null,
      isHeld: false,
      isExcluded: false,
      exclusionReasons: [],
      stageId: null,
      stageName: null,
      stageState: null,
      stageStartTime: null,
      stageEndTime: null,
      stageQty: null,
      hasDrift: false,
      orderId: null,
      orderStatus: null,
      filledQty: null,
      avgFillPrice: null,
      commission: null,
    };
  });
}
